import { useEffect, useMemo, useState } from 'react'
import { Calendar, Clock, Filter, MapPin, Search, TrendingUp, Users, X } from 'lucide-react'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { PreferencesBanner } from '@/components/PreferencesBanner'
import { UserPreferencesModal } from '@/components/UserPreferencesModal'
import { MarketplaceLocationPrompt } from '@/components/MarketplaceLocationPrompt'
import { DayUseOccurrenceDetailModal } from '@/components/DayUseOccurrenceDetailModal'
import { DayUseFilterModal } from '@/components/DayUseFilterModal'
import { modalitiesManager } from '@/managers/modalitiesManager'
import { dayUseService } from '@/services/dayUseService'
import { formatCurrency, formatDateForAPI, formatDateForUI, formatTimeForUI } from '@meiuca/web-foundation'
import { useUserLocation } from '@/hooks/useUserLocation'
import { useAuth } from '@/contexts/AuthContext'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import type { HomeUpcomingDayUseOccurrenceItem } from '@/types/sdui'
import type { Modality } from '@/types/modality'
import type { SportModality } from '@/types/address'
import { analyticsService } from '@meiuca/analytics'

export function DayUseSearchPage() {
  const { isAuthenticated } = useAuth()
  const { savePreferences, shouldShowModal } = useUserPreferences({
    autoLoad: true,
    isAuthenticated
  })

  // Track screen view on mount
  useEffect(() => {
    analyticsService.trackScreenView('day_use_search')
  }, [])

  const [searchQuery, setSearchQuery] = useState('')
  const [occurrences, setOccurrences] = useState<HomeUpcomingDayUseOccurrenceItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedOccurrenceId, setSelectedOccurrenceId] = useState<string | null>(null)
  const { userLocation, hasLocation } = useUserLocation()
  const [locationPromptOpen, setLocationPromptOpen] = useState(false)

  const [modalities, setModalities] = useState<Modality[]>([])
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false)
  const [selectedModalities, setSelectedModalities] = useState<Set<string>>(new Set())
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [tempSelectedModalities, setTempSelectedModalities] = useState<Set<string>>(new Set())
  const [tempSelectedDate, setTempSelectedDate] = useState<string | null>(null)

  // Preferences modal
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false)
  const showBanner = shouldShowModal

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const loadedModalities = await modalitiesManager.getModalities()
        setModalities(loadedModalities)
      } catch (err) {
        console.error('[DayUseSearchPage] Error loading modalities:', err)
      }
    }
    loadFilters()
  }, [])

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadOccurrences()
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, selectedModalities, selectedDate, userLocation])

  const loadOccurrences = async () => {
    if (!userLocation?.coordinate) {
      setOccurrences([])
      setError(null)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      const dateParam = selectedDate
        ? formatDateForAPI(new Date(`${selectedDate}T00:00:00`))
        : undefined

      const response = await dayUseService.searchDayUseOccurrences({
        query: searchQuery || undefined,
        date: dateParam,
        modalities: selectedModalities.size > 0 ? Array.from(selectedModalities) : undefined,
        lat: userLocation.coordinate.latitude,
        lng: userLocation.coordinate.longitude,
      })

      setOccurrences(response)

      // Track search performed
      analyticsService.track('day_use_search_search_performed', {
        search_query: searchQuery || '(empty)',
        date: selectedDate || undefined,
        modalities_count: selectedModalities.size,
        modalities: selectedModalities.size > 0 ? Array.from(selectedModalities).join(',') : undefined,
        results_count: response.length
      })
    } catch (err) {
      console.error('[DayUseSearchPage] Error loading occurrences:', err)
      setError('Erro ao carregar rotativos. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  const selectedFiltersCount = selectedModalities.size + (selectedDate ? 1 : 0)

  const sortedOccurrences = useMemo(
    () => [...occurrences].sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()),
    [occurrences]
  )

  const handleOpenFilterModal = () => {
    analyticsService.track('day_use_search_filter_opened', {})
    setTempSelectedModalities(new Set(selectedModalities))
    setTempSelectedDate(selectedDate)
    setIsFilterModalOpen(true)
  }

  const handleApplyFilters = (modalityIds: Set<string>, date: string | null) => {
    analyticsService.track('day_use_search_filter_applied', {
      modalities_count: modalityIds.size,
      modalities: modalityIds.size > 0 ? Array.from(modalityIds).join(',') : undefined,
      date: date || undefined
    })
    setSelectedModalities(new Set(modalityIds))
    setSelectedDate(date)
    setIsFilterModalOpen(false)
  }

  const handleClearFilters = () => {
    analyticsService.track('day_use_search_filter_cleared', {
      previous_modalities_count: selectedModalities.size,
      previous_date: selectedDate || undefined
    })
    setTempSelectedModalities(new Set())
    setTempSelectedDate(null)
    setSelectedModalities(new Set())
    setSelectedDate(null)
    setIsFilterModalOpen(false)
  }

  const handleOccurrenceClick = (occurrence: HomeUpcomingDayUseOccurrenceItem) => {
    analyticsService.track('day_use_search_card_clicked', {
      occurrence_id: occurrence.id,
      venue_name: occurrence.venueName,
      modality: occurrence.modality,
      price: occurrence.pricePerPersonCents,
      available_spots: occurrence.availableSpots
    })
    setSelectedOccurrenceId(occurrence.id)
  }

  const handleRemoveFilter = (type: 'modality' | 'date', id?: string, label?: string) => {
    analyticsService.track('day_use_search_filter_removed', {
      filter_type: type,
      filter_id: id,
      filter_label: label
    })

    if (type === 'date') {
      setSelectedDate(null)
    } else if (id) {
      const newSet = new Set(selectedModalities)
      newSet.delete(id)
      setSelectedModalities(newSet)
    }
  }

  const handleClearAllFilters = () => {
    analyticsService.track('day_use_search_filter_cleared', {
      previous_modalities_count: selectedModalities.size,
      previous_date: selectedDate || undefined,
      source: 'clear_all_button'
    })
    setSelectedModalities(new Set())
    setSelectedDate(null)
  }

  const handlePreferencesSave = async (modalities: SportModality[]) => {
    await savePreferences(modalities)
    setPreferencesModalOpen(false)
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      <MarketplaceHeader onLocationClick={() => setLocationPromptOpen(true)} />

      {/* Preferences Banner */}
      {showBanner && (
        <PreferencesBanner onOpenModal={() => setPreferencesModalOpen(true)} />
      )}

      {/* User Preferences Modal */}
      <UserPreferencesModal
        isOpen={preferencesModalOpen}
        onClose={() => setPreferencesModalOpen(false)}
        onSave={handlePreferencesSave}
        title="Quais esportes você pratica?"
        description="Conte pra gente para vermos as melhores arenas para você"
        allowSkip={true}
      />

      <MarketplaceLocationPrompt
        open={locationPromptOpen}
        onClose={() => setLocationPromptOpen(false)}
      />

      <div className="sticky top-16 z-40 bg-background-secondary border-b border-border-light shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Buscar por arena ou local..."
                className="w-full pl-12 pr-4 py-3 border border-border-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-text-primary placeholder:text-text-tertiary"
              />
            </div>

            <button
              onClick={handleOpenFilterModal}
              className="flex items-center gap-2 px-4 py-3 border border-border-primary rounded-lg hover:bg-background-tertiary transition-colors relative"
            >
              <Filter className="w-5 h-5 text-text-secondary" />
              <span className="text-text-primary font-medium">Filtros</span>
              {selectedFiltersCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {selectedFiltersCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {(selectedModalities.size > 0 || selectedDate) && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-text-secondary font-medium">Filtros ativos:</span>
            {selectedDate && (
              <button
                onClick={() => handleRemoveFilter('date', undefined, formatDateForUI(selectedDate, 'short'))}
                className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors"
              >
                {formatDateForUI(selectedDate, 'short')}
                <X className="w-3 h-3" />
              </button>
            )}
            {Array.from(selectedModalities).map((modalityId) => {
              const modality = modalities.find((item) => item.id === modalityId)
              if (!modality) return null

              return (
                <button
                  key={`modality-${modalityId}`}
                  onClick={() => handleRemoveFilter('modality', modalityId, modality.description)}
                  className="flex items-center gap-1 px-3 py-1 bg-brand-primary/10 text-brand-primary rounded-full text-sm font-medium hover:bg-brand-primary/20 transition-colors"
                >
                  {modality.description}
                  <X className="w-3 h-3" />
                </button>
              )
            })}
            <button
              onClick={handleClearAllFilters}
              className="text-sm text-text-tertiary hover:text-text-primary underline"
            >
              Limpar tudo
            </button>
          </div>
        )}

        <div className="mb-4">
          <p className="text-sm text-text-secondary">
            {isLoading
              ? 'Buscando...'
              : `${sortedOccurrences.length} ${sortedOccurrences.length === 1 ? 'rotativo encontrado' : 'rotativos encontrados'}`}
          </p>
        </div>

        {!isLoading && !hasLocation && (
          <div className="py-12">
            <div className="relative overflow-hidden rounded-2xl border border-border-light bg-white p-8 text-center shadow-sm">
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(0,0,0,0.03),_transparent_60%)]" />
              <div className="relative space-y-4">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-primary/10 text-brand-primary">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-text-primary">Escolha sua localizacao</p>
                  <p className="text-sm text-text-secondary">
                    Precisamos dela para mostrar os rotativos mais proximos de voce.
                  </p>
                </div>
                <button
                  onClick={() => setLocationPromptOpen(true)}
                  className="inline-flex items-center justify-center rounded-lg bg-brand-primary px-5 py-2 text-sm font-semibold text-white hover:bg-brand-primary/90 transition-colors"
                >
                  Selecionar endereco
                </button>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={loadOccurrences}
              className="text-sm text-brand-primary hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {!error && hasLocation && sortedOccurrences.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedOccurrences.map((occurrence) => {
              const isUnlimited = occurrence.maxPeople === null || occurrence.maxPeople === undefined
              const occupancyPercentage = !isUnlimited && occurrence.maxPeople
                ? Math.round((occurrence.totalParticipants / occurrence.maxPeople) * 100)
                : null
              const isAlmostFull = occupancyPercentage !== null && occupancyPercentage >= 80
              const isFull = !isUnlimited && occurrence.availableSpots === 0

              return (
                <div
                  key={occurrence.id}
                  className="group relative overflow-hidden rounded-lg border border-border-light bg-white hover:border-brand-primary hover:shadow-md transition-all duration-200 cursor-pointer"
                  onClick={() => handleOccurrenceClick(occurrence)}
                >
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      {occurrence.venueLogoUrl ? (
                        <img
                          src={occurrence.venueLogoUrl}
                          alt={occurrence.venueName}
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-lg bg-brand-primary/10 text-brand-primary flex items-center justify-center font-bold">
                          {occurrence.venueName.charAt(0)}
                        </div>
                      )}
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-text-primary">{occurrence.venueName}</p>
                        {occurrence.venueLocation && (
                          <div className="flex items-center gap-1 text-xs text-text-secondary">
                            <MapPin className="h-3.5 w-3.5" />
                            <span>{occurrence.venueLocation}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary/10 px-3 py-1 text-xs font-medium text-brand-primary">
                      {modalitiesManager.getModalityDescription(occurrence.modality)}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-text-primary">
                      <Calendar className="h-4 w-4 text-brand-primary" />
                      <span>{formatDateForUI(occurrence.startsAt, 'medium')}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-text-primary">
                      <Clock className="h-4 w-4 text-text-secondary" />
                      <span>
                        {formatTimeForUI(occurrence.startsAt)} - {formatTimeForUI(occurrence.endsAt)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 border-t border-border-light pt-3">
                      <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Users className="h-4 w-4" />
                        <span>
                          {occurrence.totalParticipants} pessoa{occurrence.totalParticipants === 1 ? '' : 's'}
                          {isUnlimited ? ' (ilimitado)' : ` / ${occurrence.maxPeople ?? occurrence.minPeople}`}
                        </span>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-brand-primary">
                          {formatCurrency(occurrence.pricePerPersonCents)}
                        </div>
                        <div className="text-xs text-text-secondary">por pessoa</div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TrendingUp className={`h-4 w-4 ${
                          isUnlimited
                            ? 'text-blue-500'
                            : isFull
                            ? 'text-red-500'
                            : isAlmostFull
                            ? 'text-orange-500'
                            : 'text-green-500'
                        }`} />
                        <span className={`text-sm font-medium ${
                          isUnlimited
                            ? 'text-blue-600'
                            : isFull
                            ? 'text-red-600'
                            : isAlmostFull
                            ? 'text-orange-600'
                            : 'text-green-600'
                        }`}>
                          {isUnlimited
                            ? 'Vagas ilimitadas'
                            : isFull
                            ? 'Lotado'
                            : `${occurrence.availableSpots} ${occurrence.availableSpots === 1 ? 'vaga' : 'vagas'}`}
                        </span>
                      </div>
                      {occupancyPercentage !== null ? (
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          isFull
                            ? 'bg-red-100 text-red-700'
                            : isAlmostFull
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {occupancyPercentage}% ocupado
                        </div>
                      ) : (
                        <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                          Sem limite
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {!error && !isLoading && hasLocation && sortedOccurrences.length === 0 && (
          <div className="text-center py-12">
            <p className="text-text-secondary mb-2">Nenhum rotativo encontrado</p>
            <p className="text-sm text-text-tertiary">Tente ajustar sua busca</p>
          </div>
        )}
      </div>

      <DayUseFilterModal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        modalities={modalities}
        selectedModalityIds={tempSelectedModalities}
        selectedDate={tempSelectedDate}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      <DayUseOccurrenceDetailModal
        occurrenceId={selectedOccurrenceId}
        isOpen={!!selectedOccurrenceId}
        onClose={() => setSelectedOccurrenceId(null)}
      />
    </div>
  )
}
